// Registry canônico de capabilities do Game Pro Hub.
// O agente consulta este mapa — nunca decide por palpite o que o produto faz.
//
// requiresPlan:
//   'any'            → qualquer usuário autenticado
//   'free_limited'   → Free até freeLimit usos por conta; Pro ilimitado (fair use)
//   'pro_or_legacy'  → Pro, ou entitlement vitalício legado listado em legacyEntitlementKeys
//   'admin'          → claim/role admin verificado
//   'partner'        → role partner verificado
//
// type: 'skill' | 'full_screen' | 'admin' | 'purchase' | 'support' | 'fallback'

export const FREE_LIMIT_DEFAULT = 2;

export const CAPABILITIES = {
  sensitivity_diagnostic: {
    capabilityId: 'sensitivity_diagnostic',
    internalName: 'Sensi engine',
    friendlyName: 'Vamos corrigir sua sensibilidade',
    type: 'skill',
    adapter: 'SensitivitySkillAdapter',
    intents: ['sensitivity_problem', 'aim_problem'],
    keywords: [
      'mira ruim', 'mira sobe', 'mira desce', 'mira treme', 'mira passa', 'mira tremendo',
      'sensibilidade', 'sensi', 'recoil', 'spray abre', 'spray sobe', 'controle de spray',
      'nao acompanha', 'não acompanha', 'mira estranha', 'ajustar mira', 'corrigir mira',
      'gyro', 'giroscopio', 'giroscópio'
    ],
    requiresPlan: 'pro_or_legacy',
    freeLimit: 0,
    legacyEntitlementKeys: [],
    supportedGames: ['pubg_mobile', 'free_fire', 'codm', 'other'],
    requiredSlots: ['symptom'],
    clarifyingQuestion: {
      question: 'Entendi. Preciso saber como ela está ruim para montar a correção certa.',
      options: [
        { label: 'Sobe quando atiro', value: 'sobe' },
        { label: 'Cai rápido', value: 'cai' },
        { label: 'Treme para os lados', value: 'treme' },
        { label: 'Passa do alvo', value: 'passa' },
        { label: 'Não acompanha o inimigo', value: 'nao_acompanha' },
        { label: 'Não sei explicar', value: 'nao_sei' }
      ],
      slot: 'symptom'
    },
    nextSteps: ['mi_analysis_aim', 'calculator_auto', 'training_single_drill', 'restart'],
    helpText: 'Responda as perguntas do diagnóstico. No final você recebe o ajuste recomendado e como validar.'
  },

  calculator_pubg: {
    capabilityId: 'calculator_pubg',
    internalName: 'PUBG calculator',
    friendlyName: 'Calcular valores das outras miras',
    type: 'skill',
    adapter: 'CalculatorSkillAdapter',
    intents: ['calculator_request'],
    keywords: ['calculadora pubg', 'calcular pubg', 'miras pubg'],
    requiresPlan: 'pro_or_legacy',
    freeLimit: 0,
    legacyEntitlementKeys: ['calculatorLifetime'],
    supportedGames: ['pubg_mobile'],
    requiredSlots: ['game'],
    nextSteps: ['mi_analysis_aim', 'sensitivity_diagnostic', 'restart'],
    unsupportedFallback:
      'Essa skill é exclusiva do Pro ou de usuários que já compraram a calculadora vitalícia.',
    helpText: 'Informe sua mira base e o valor atual. O GPH calcula os outros escopos com a engine oficial.'
  },

  calculator_freefire: {
    capabilityId: 'calculator_freefire',
    internalName: 'Free Fire calculator',
    friendlyName: 'Calcular valores das outras miras',
    type: 'skill',
    adapter: 'CalculatorSkillAdapter',
    intents: ['calculator_request'],
    keywords: ['calculadora free fire', 'calcular free fire', 'miras free fire', 'calculadora ff'],
    requiresPlan: 'pro_or_legacy',
    freeLimit: 0,
    legacyEntitlementKeys: ['freefireCalculatorLifetime'],
    supportedGames: ['free_fire'],
    requiredSlots: ['game'],
    nextSteps: ['mi_analysis_aim', 'sensitivity_diagnostic', 'restart'],
    unsupportedFallback:
      'Essa skill é exclusiva do Pro ou de usuários que já compraram a calculadora vitalícia.',
    helpText: 'Informe sua sensibilidade base. O GPH calcula os demais valores com a engine oficial.'
  },

  calculator_codm_unsupported: {
    capabilityId: 'calculator_codm_unsupported',
    internalName: 'CODM calculator (unavailable)',
    friendlyName: 'Cálculo CODM indisponível',
    type: 'fallback',
    adapter: null,
    intents: ['calculator_request'],
    keywords: ['calculadora codm', 'calcular codm', 'mira codm', 'cod mobile'],
    requiresPlan: 'any',
    freeLimit: 0,
    legacyEntitlementKeys: [],
    supportedGames: ['codm'],
    requiredSlots: [],
    nextSteps: ['sensitivity_diagnostic', 'training_single_drill', 'mi_analysis_gameplay', 'restart'],
    helpText: null
  },

  mi_analysis_gameplay: {
    capabilityId: 'mi_analysis_gameplay',
    internalName: 'Mi Análise (gameplay)',
    friendlyName: 'Vamos entender sua gameplay',
    type: 'skill',
    adapter: 'MiAnalysisSkillAdapter',
    intents: ['video_analysis'],
    keywords: ['analisar gameplay', 'analise geral', 'análise geral', 'minha gameplay', 'analisar video', 'analisar vídeo', 'mandar video', 'enviar video', 'enviar vídeo'],
    requiresPlan: 'pro_or_legacy',
    freeLimit: 0,
    legacyEntitlementKeys: [],
    supportedGames: ['pubg_mobile', 'free_fire', 'codm', 'other'],
    requiredSlots: ['analysisGoal'],
    nextSteps: ['training_single_drill', 'sensitivity_diagnostic', 'restart'],
    helpText: 'Envie o vídeo e confirme o objetivo. A análise roda na engine oficial da Mi Análise.'
  },

  mi_analysis_replay: {
    capabilityId: 'mi_analysis_replay',
    internalName: 'Mi Análise (replay)',
    friendlyName: 'Vamos descobrir o erro principal do replay',
    type: 'skill',
    adapter: 'MiAnalysisSkillAdapter',
    intents: ['video_analysis'],
    keywords: ['replay', 'lance', 'analisar lance'],
    requiresPlan: 'pro_or_legacy',
    freeLimit: 0,
    legacyEntitlementKeys: [],
    supportedGames: ['pubg_mobile', 'free_fire', 'codm', 'other'],
    requiredSlots: ['analysisGoal'],
    nextSteps: ['training_single_drill', 'sensitivity_diagnostic', 'restart'],
    helpText: 'Envie o replay e confirme o que quer descobrir nele.'
  },

  mi_analysis_x1: {
    capabilityId: 'mi_analysis_x1',
    internalName: 'Mi Análise (x1)',
    friendlyName: 'Vamos analisar esse x1',
    type: 'skill',
    adapter: 'MiAnalysisSkillAdapter',
    intents: ['video_analysis', 'x1_lost'],
    keywords: ['perdi x1', 'perdi o x1', 'perdi um x1', 'perdi duelo', 'perdi a troca', 'x1'],
    requiresPlan: 'pro_or_legacy',
    freeLimit: 0,
    legacyEntitlementKeys: [],
    supportedGames: ['pubg_mobile', 'free_fire', 'codm', 'other'],
    requiredSlots: ['analysisGoal'],
    nextSteps: ['training_single_drill', 'sensitivity_diagnostic', 'restart'],
    helpText: 'Envie o vídeo do x1. O GPH foca em decisão de combate, movimentação e mira.'
  },

  mi_analysis_aim: {
    capabilityId: 'mi_analysis_aim',
    internalName: 'Mi Análise (mira)',
    friendlyName: 'Vamos validar sua mira no vídeo',
    type: 'skill',
    adapter: 'MiAnalysisSkillAdapter',
    intents: ['video_analysis'],
    keywords: ['validar mira', 'validar sensibilidade', 'mira no video', 'mira no vídeo'],
    requiresPlan: 'pro_or_legacy',
    freeLimit: 0,
    legacyEntitlementKeys: [],
    supportedGames: ['pubg_mobile', 'free_fire', 'codm', 'other'],
    requiredSlots: ['analysisGoal'],
    nextSteps: ['sensitivity_diagnostic', 'calculator_auto', 'restart'],
    helpText: 'Envie um vídeo com sprays e trocas. O foco da análise será mira e sensibilidade.'
  },

  mi_analysis_movement: {
    capabilityId: 'mi_analysis_movement',
    internalName: 'Mi Análise (movimentação)',
    friendlyName: 'Vamos analisar sua movimentação',
    type: 'skill',
    adapter: 'MiAnalysisSkillAdapter',
    intents: ['video_analysis'],
    keywords: ['movimentacao', 'movimentação', 'posicionamento', 'me posiciono mal'],
    requiresPlan: 'pro_or_legacy',
    freeLimit: 0,
    legacyEntitlementKeys: [],
    supportedGames: ['pubg_mobile', 'free_fire', 'codm', 'other'],
    requiredSlots: ['analysisGoal'],
    nextSteps: ['training_single_drill', 'restart'],
    helpText: 'Envie o vídeo. O foco da análise será posicionamento e movimentação.'
  },

  training_single_drill: {
    capabilityId: 'training_single_drill',
    internalName: 'Coach V2 (drill único)',
    friendlyName: 'Treino específico de jogada',
    type: 'skill',
    adapter: 'TrainingSkillAdapter',
    intents: ['training_request'],
    keywords: [
      'treinar', 'treino', 'quero treinar', 'strafe', 'rush', 'escada', 'pincamento', 'pinçamento',
      'repeek', 'smoke', 'bait', 'entrada em casa', 'defender', 'melhorar movimentacao', 'melhorar movimentação',
      'jogada', 'drill'
    ],
    requiresPlan: 'pro_or_legacy',
    freeLimit: 0,
    legacyEntitlementKeys: [],
    supportedGames: ['pubg_mobile', 'free_fire', 'codm', 'other'],
    requiredSlots: ['drillGoal'],
    clarifyingQuestion: {
      question: 'Fechou. Qual situação você quer treinar?',
      options: [
        { label: 'Strafe / troca curta', value: 'strafe' },
        { label: 'Defender rush', value: 'rush' },
        { label: 'X1 dentro de casa', value: 'x1_casa' },
        { label: 'Sair de pinçamento', value: 'pincamento' },
        { label: 'Movimentação geral', value: 'movimentacao' },
        { label: 'Outra jogada', value: 'outra' }
      ],
      slot: 'drillGoal'
    },
    nextSteps: ['training_favorites', 'mi_analysis_gameplay', 'restart'],
    helpText: 'Siga o passo a passo do treino. Favorite para acessar depois e valide com vídeo quando estiver pronto.'
  },

  training_favorites: {
    capabilityId: 'training_favorites',
    internalName: 'Favoritos',
    friendlyName: 'Meus treinos favoritos',
    type: 'full_screen',
    adapter: 'FavoritesSkillAdapter',
    intents: ['favorites_request'],
    keywords: ['favoritos', 'meus treinos', 'treinos salvos', 'favoritei'],
    requiresPlan: 'any',
    freeLimit: 0,
    legacyEntitlementKeys: [],
    supportedGames: ['pubg_mobile', 'free_fire', 'codm', 'other'],
    requiredSlots: [],
    nextSteps: ['training_single_drill', 'restart'],
    helpText: 'Toque em um treino favorito para abrir de novo.'
  },

  profile_settings: {
    capabilityId: 'profile_settings',
    internalName: 'Configurações',
    friendlyName: 'Configurações',
    type: 'full_screen',
    adapter: 'ProfileSettingsAdapter',
    intents: ['settings_request'],
    keywords: ['configuracoes', 'configurações', 'configuracao', 'configuração', 'ajustes da conta', 'idioma', 'trocar jogo', 'minha conta', 'sair da conta'],
    requiresPlan: 'any',
    freeLimit: 0,
    legacyEntitlementKeys: [],
    supportedGames: ['pubg_mobile', 'free_fire', 'codm', 'other'],
    requiredSlots: [],
    nextSteps: ['profile_plan', 'restart'],
    helpText: 'Aqui você gerencia conta, jogo principal e preferências.'
  },

  profile_plan: {
    capabilityId: 'profile_plan',
    internalName: 'Plano e limites',
    friendlyName: 'Meu plano e limites',
    type: 'full_screen',
    adapter: 'ProfilePlanAdapter',
    intents: ['plan_request'],
    keywords: ['meu plano', 'meus limites', 'ver plano', 'cancelar', 'assinatura', 'quanto falta', 'limite free'],
    requiresPlan: 'any',
    freeLimit: 0,
    legacyEntitlementKeys: [],
    supportedGames: ['pubg_mobile', 'free_fire', 'codm', 'other'],
    requiredSlots: [],
    nextSteps: ['purchase_pro', 'profile_settings', 'restart'],
    helpText: 'Veja seu plano atual, limites de uso e acessos vitalícios legados.'
  },

  purchase_pro: {
    capabilityId: 'purchase_pro',
    internalName: 'Compra Pro',
    friendlyName: 'Assinar Pro',
    type: 'purchase',
    adapter: 'PurchaseProAdapter',
    intents: ['purchase_request'],
    keywords: ['assinar pro', 'virar pro', 'quero pro', 'comprar pro', 'plano pro', 'upgrade', 'assinar', 'preço', 'preco', 'quanto custa'],
    requiresPlan: 'any',
    freeLimit: 0,
    legacyEntitlementKeys: [],
    supportedGames: ['pubg_mobile', 'free_fire', 'codm', 'other'],
    requiredSlots: [],
    nextSteps: ['profile_plan', 'restart'],
    helpText: 'Revise os benefícios e conclua no checkout seguro existente.'
  },

  support_community: {
    capabilityId: 'support_community',
    internalName: 'Suporte e comunidade',
    friendlyName: 'Suporte e comunidade',
    type: 'support',
    adapter: 'SupportCommunityAdapter',
    intents: ['support_request'],
    keywords: ['suporte', 'ajuda', 'comunidade', 'falar com alguem', 'falar com alguém', 'problema na conta', 'bug', 'erro no app'],
    requiresPlan: 'any',
    freeLimit: 0,
    legacyEntitlementKeys: [],
    supportedGames: ['pubg_mobile', 'free_fire', 'codm', 'other'],
    requiredSlots: [],
    nextSteps: ['profile_plan', 'restart'],
    helpText: 'Entre na comunidade ou veja as opções de conta.'
  },

  admin_panel: {
    capabilityId: 'admin_panel',
    internalName: 'Painel ADM',
    friendlyName: 'Painel ADM',
    type: 'admin',
    adapter: 'AdminPanelAdapter',
    intents: ['admin_request'],
    keywords: ['painel adm', 'painel admin', 'administrador', 'admin'],
    requiresPlan: 'admin',
    freeLimit: 0,
    legacyEntitlementKeys: [],
    supportedGames: ['pubg_mobile', 'free_fire', 'codm', 'other'],
    requiredSlots: [],
    nextSteps: ['restart'],
    helpText: null
  },

  partner_panel: {
    capabilityId: 'partner_panel',
    internalName: 'Painel parceiro',
    friendlyName: 'Painel parceiro',
    type: 'admin',
    adapter: 'PartnerPanelAdapter',
    intents: ['partner_request'],
    keywords: ['painel parceiro', 'parceiro', 'afiliado', 'comissao', 'comissão', 'indicacao', 'indicação'],
    requiresPlan: 'partner',
    freeLimit: 0,
    legacyEntitlementKeys: [],
    supportedGames: ['pubg_mobile', 'free_fire', 'codm', 'other'],
    requiredSlots: [],
    nextSteps: ['restart'],
    helpText: null
  },

  fallback_expert_answer: {
    capabilityId: 'fallback_expert_answer',
    internalName: 'Fallback especialista',
    friendlyName: 'Coach FPS Mobile',
    type: 'fallback',
    adapter: 'FallbackExpertAdapter',
    intents: ['fallback_expert_answer'],
    keywords: [],
    requiresPlan: 'any',
    freeLimit: 0,
    legacyEntitlementKeys: [],
    supportedGames: ['pubg_mobile', 'free_fire', 'codm', 'other'],
    requiredSlots: [],
    nextSteps: ['sensitivity_diagnostic', 'training_single_drill', 'mi_analysis_gameplay', 'restart'],
    helpText: 'Orientação de coach conduzida por conversa quando não há fluxo direto para o pedido.'
  },

  unknown_intent_fallback: {
    capabilityId: 'unknown_intent_fallback',
    internalName: 'Fallback',
    friendlyName: 'Não entendi',
    type: 'fallback',
    adapter: null,
    intents: ['unknown'],
    keywords: [],
    requiresPlan: 'any',
    freeLimit: 0,
    legacyEntitlementKeys: [],
    supportedGames: ['pubg_mobile', 'free_fire', 'codm', 'other'],
    requiredSlots: [],
    nextSteps: ['sensitivity_diagnostic', 'training_single_drill', 'mi_analysis_gameplay', 'purchase_pro'],
    helpText: null
  }
};

export const getCapability = (capabilityId) => CAPABILITIES[capabilityId] || null;

export const listCapabilities = () => Object.values(CAPABILITIES);

// Capabilities elegíveis ao limite Free (bloqueio é por skill, nunca global).
export const FREE_LIMITED_CAPABILITIES = listCapabilities()
  .filter((cap) => cap.requiresPlan === 'free_limited')
  .map((cap) => cap.capabilityId);

// Resolve a calculadora certa para o jogo ativo ('calculator_auto' nos nextSteps).
export const resolveCalculatorForGame = (canonicalGame) => {
  if (canonicalGame === 'pubg_mobile') return CAPABILITIES.calculator_pubg;
  if (canonicalGame === 'free_fire') return CAPABILITIES.calculator_freefire;
  if (canonicalGame === 'codm') return CAPABILITIES.calculator_codm_unsupported;
  return null;
};

export default CAPABILITIES;
